/**
 * Loan Data Point Registry
 *
 * Canonical registry of all extractable data points for private lending loans.
 * Used by the document pipeline for LLM extraction and API gateway for reconciliation.
 *
 * Each data point defines:
 * - Which document types it can be extracted from
 * - Which loan types require or optionally use it
 * - How conflicts between multiple extractions are resolved (survivorship rules)
 * - Optional validation constraints
 */

import type { DocumentType, PrivateLendingLoanType } from '../constants/document-types';

// ============================================================================
// SURVIVORSHIP RULES
// ============================================================================

/**
 * Rules for resolving conflicts when the same data point is extracted
 * from multiple documents with differing values.
 */
export const SURVIVORSHIP_RULES = {
  /** Use the value from the highest-priority document source */
  SOURCE_PRECEDENCE: 'SOURCE_PRECEDENCE',
  /** Use the value from the most recently dated document */
  MOST_RECENT: 'MOST_RECENT',
  /** Use the extraction with the highest confidence score */
  HIGHEST_CONFIDENCE: 'HIGHEST_CONFIDENCE',
  /** Require values from multiple sources to agree within tolerance */
  CROSS_VALIDATED: 'CROSS_VALIDATED',
  /** Values must match exactly across all sources or flag for review */
  EXACT_MATCH_REQUIRED: 'EXACT_MATCH_REQUIRED',
} as const;

export type SurvivorshipRule = (typeof SURVIVORSHIP_RULES)[keyof typeof SURVIVORSHIP_RULES];

// ============================================================================
// VALUE TYPES
// ============================================================================

/**
 * Supported value types for extracted data points.
 */
export const VALUE_TYPES = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  BOOLEAN: 'BOOLEAN',
  CURRENCY: 'CURRENCY',
} as const;

export type ValueType = (typeof VALUE_TYPES)[keyof typeof VALUE_TYPES];

// ============================================================================
// DATA POINT CATEGORIES
// ============================================================================

/**
 * Logical groupings for loan data points.
 */
export const DATA_POINT_CATEGORIES = {
  BORROWER: 'BORROWER',
  PROPERTY: 'PROPERTY',
  FINANCIAL: 'FINANCIAL',
  LEGAL: 'LEGAL',
  EMPLOYMENT: 'EMPLOYMENT',
} as const;

export type DataPointCategory = (typeof DATA_POINT_CATEGORIES)[keyof typeof DATA_POINT_CATEGORIES];

// ============================================================================
// LOAN DATA POINT INTERFACE
// ============================================================================

/**
 * Definition of a single extractable loan data point.
 */
export interface LoanDataPoint {
  /** Unique canonical key used across extraction, reconciliation, and storage */
  canonicalKey: string;
  /** Human-readable display name */
  displayName: string;
  /** Logical category for grouping */
  category: DataPointCategory;
  /** Expected value type for normalization */
  valueType: ValueType;
  /** Loan types where this data point is required */
  requiredFor: PrivateLendingLoanType[];
  /** Loan types where this data point is optional */
  optionalFor: PrivateLendingLoanType[];
  /** Document types from which this data point can be extracted */
  extractableFrom: DocumentType[];
  /** Rule for resolving conflicting values across multiple extractions */
  survivorshipRule: SurvivorshipRule;
  /** Optional validation constraints */
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    format?: string;
  };
}

// ============================================================================
// SHARED LOAN TYPE GROUPS
// ============================================================================

/** Major loan types that require most borrower/property/legal fields */
const ALL_MAJOR_LOAN_TYPES: PrivateLendingLoanType[] = [
  'bridge',
  'fix_and_flip',
  'dscr',
  'ground_up',
  'commercial',
  'multifamily',
];

/** Loan types focused on rental/income properties */
const RENTAL_LOAN_TYPES: PrivateLendingLoanType[] = ['dscr', 'commercial', 'multifamily'];

// ============================================================================
// LOAN DATA POINTS REGISTRY
// ============================================================================

/**
 * Complete registry of extractable loan data points.
 *
 * ~35 canonical data points organized by category, each defining:
 * - Where they can be extracted from (document types)
 * - Where they are needed (loan types)
 * - How conflicts are resolved (survivorship rules)
 */
export const LOAN_DATA_POINTS: LoanDataPoint[] = [
  // --------------------------------------------------------------------------
  // BORROWER CATEGORY
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'borrower_name',
    displayName: 'Borrower Full Name',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['drivers_license', 'tax_return_personal', 'bank_statement_personal'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
  },
  {
    canonicalKey: 'borrower_address',
    displayName: 'Borrower Current Address',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['drivers_license', 'utility_bill', 'bank_statement_personal'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'ssn',
    displayName: 'Social Security Number',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['tax_return_personal', 'ssn_card'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
    validation: {
      pattern: '^\\d{3}-?\\d{2}-?\\d{4}$',
    },
  },
  {
    canonicalKey: 'dob',
    displayName: 'Date of Birth',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.DATE,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['drivers_license', 'passport'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
  },
  {
    canonicalKey: 'email',
    displayName: 'Email Address',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'phone',
    displayName: 'Phone Number',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'citizenship_status',
    displayName: 'Citizenship Status',
    category: DATA_POINT_CATEGORIES.BORROWER,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.SOURCE_PRECEDENCE,
  },

  // --------------------------------------------------------------------------
  // EMPLOYMENT CATEGORY
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'employer_name',
    displayName: 'Employer Name',
    category: DATA_POINT_CATEGORIES.EMPLOYMENT,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: ['income_verification'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'employment_position',
    displayName: 'Employment Position/Title',
    category: DATA_POINT_CATEGORIES.EMPLOYMENT,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: ['income_verification'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'years_employed',
    displayName: 'Years at Current Employer',
    category: DATA_POINT_CATEGORIES.EMPLOYMENT,
    valueType: VALUE_TYPES.NUMBER,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: ['income_verification'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },

  // --------------------------------------------------------------------------
  // FINANCIAL CATEGORY
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'annual_income',
    displayName: 'Annual Income',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: ['tax_return_personal', 'income_verification'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'monthly_income',
    displayName: 'Monthly Income',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: ['bank_statement_personal', 'income_verification'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'monthly_debt',
    displayName: 'Monthly Debt Obligations',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.NUMBER,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'credit_score',
    displayName: 'Credit Score',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.NUMBER,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
    validation: {
      min: 300,
      max: 850,
    },
  },
  {
    canonicalKey: 'total_assets',
    displayName: 'Total Assets',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['bank_statement_personal', 'bank_statement_business'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'total_liabilities',
    displayName: 'Total Liabilities',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'dti_ratio',
    displayName: 'Debt-to-Income Ratio',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.NUMBER,
    requiredFor: ['bridge', 'fix_and_flip', 'ground_up', 'commercial', 'multifamily'],
    optionalFor: ['dscr'],
    extractableFrom: [],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
    validation: {
      min: 0,
      max: 100,
    },
  },
  {
    canonicalKey: 'account_balance',
    displayName: 'Account Balance',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['bank_statement_personal', 'bank_statement_business'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
  },
  {
    canonicalKey: 'total_deposits',
    displayName: 'Total Deposits',
    category: DATA_POINT_CATEGORIES.FINANCIAL,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['bank_statement_personal', 'bank_statement_business'],
    survivorshipRule: SURVIVORSHIP_RULES.CROSS_VALIDATED,
  },

  // --------------------------------------------------------------------------
  // PROPERTY CATEGORY
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'property_address',
    displayName: 'Property Address',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['property_appraisal', 'purchase_agreement'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
  },
  {
    canonicalKey: 'property_type',
    displayName: 'Property Type',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['property_appraisal'],
    survivorshipRule: SURVIVORSHIP_RULES.SOURCE_PRECEDENCE,
  },
  {
    canonicalKey: 'purchase_price',
    displayName: 'Purchase Price',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['purchase_agreement'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
  },
  {
    canonicalKey: 'after_repair_value',
    displayName: 'After Repair Value (ARV)',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ['fix_and_flip', 'ground_up'],
    optionalFor: ['bridge', 'dscr', 'commercial', 'multifamily'],
    extractableFrom: ['property_appraisal'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'current_value',
    displayName: 'Current Appraised Value',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['property_appraisal'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },
  {
    canonicalKey: 'renovation_budget',
    displayName: 'Renovation Budget',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: ['fix_and_flip', 'ground_up'],
    optionalFor: ['bridge'],
    extractableFrom: ['scope_of_work', 'construction_budget'],
    survivorshipRule: SURVIVORSHIP_RULES.SOURCE_PRECEDENCE,
  },
  {
    canonicalKey: 'rental_income',
    displayName: 'Rental Income',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: RENTAL_LOAN_TYPES,
    optionalFor: ['bridge'],
    extractableFrom: ['rent_roll', 'lease_agreements'],
    survivorshipRule: SURVIVORSHIP_RULES.CROSS_VALIDATED,
  },
  {
    canonicalKey: 'occupancy_rate',
    displayName: 'Occupancy Rate',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.NUMBER,
    requiredFor: RENTAL_LOAN_TYPES,
    optionalFor: ['bridge'],
    extractableFrom: ['rent_roll'],
    survivorshipRule: SURVIVORSHIP_RULES.MOST_RECENT,
    validation: {
      min: 0,
      max: 100,
    },
  },
  {
    canonicalKey: 'noi',
    displayName: 'Net Operating Income (NOI)',
    category: DATA_POINT_CATEGORIES.PROPERTY,
    valueType: VALUE_TYPES.CURRENCY,
    requiredFor: RENTAL_LOAN_TYPES,
    optionalFor: ['bridge'],
    extractableFrom: ['rent_roll', 'financial_statement'],
    survivorshipRule: SURVIVORSHIP_RULES.HIGHEST_CONFIDENCE,
  },

  // --------------------------------------------------------------------------
  // LEGAL CATEGORY
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'entity_name',
    displayName: 'Borrowing Entity Name',
    category: DATA_POINT_CATEGORIES.LEGAL,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['operating_agreement', 'articles_of_incorporation'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
  },
  {
    canonicalKey: 'entity_type',
    displayName: 'Entity Type',
    category: DATA_POINT_CATEGORIES.LEGAL,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['operating_agreement', 'articles_of_incorporation'],
    survivorshipRule: SURVIVORSHIP_RULES.SOURCE_PRECEDENCE,
  },
  {
    canonicalKey: 'ein',
    displayName: 'Employer Identification Number (EIN)',
    category: DATA_POINT_CATEGORIES.LEGAL,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['ein_letter', 'tax_return_business'],
    survivorshipRule: SURVIVORSHIP_RULES.EXACT_MATCH_REQUIRED,
    validation: {
      pattern: '^\\d{2}-?\\d{7}$',
    },
  },
  {
    canonicalKey: 'state_of_formation',
    displayName: 'State of Formation',
    category: DATA_POINT_CATEGORIES.LEGAL,
    valueType: VALUE_TYPES.STRING,
    requiredFor: ALL_MAJOR_LOAN_TYPES,
    optionalFor: [],
    extractableFrom: ['operating_agreement', 'good_standing_certificate'],
    survivorshipRule: SURVIVORSHIP_RULES.SOURCE_PRECEDENCE,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all data points relevant to a specific loan type, split into required and optional.
 *
 * @param loanType - The loan type to filter by
 * @returns Object containing required and optional data point arrays
 */
export function getDataPointsForLoanType(loanType: PrivateLendingLoanType): {
  required: LoanDataPoint[];
  optional: LoanDataPoint[];
} {
  const required = LOAN_DATA_POINTS.filter((dp) => dp.requiredFor.includes(loanType));
  const optional = LOAN_DATA_POINTS.filter((dp) => dp.optionalFor.includes(loanType));
  return { required, optional };
}

/**
 * Look up a single data point by its canonical key.
 *
 * @param canonicalKey - The unique key identifying the data point
 * @returns The matching LoanDataPoint, or undefined if not found
 */
export function getDataPointByKey(canonicalKey: string): LoanDataPoint | undefined {
  return LOAN_DATA_POINTS.find((dp) => dp.canonicalKey === canonicalKey);
}
